import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { AgGridTable } from "../../Component/AgGridTable";
import { ColDef } from "ag-grid-community";
import {
  getTimeSheetDetailsById,
  UpdateTimesheet,
} from "../../services/timesheetService";
import StickyFooterButtons from "../../Component/StickyFooterButtons";
import { Timeline } from "@mui/icons-material";
import InvoiceSummaryCard from "../../Component/Summarycard";
import { deleteTimesheetById } from "../../services/timesheetService";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmationDialog from "../../Component/ConfirmationDialog";
type EditTableData = {
  employeeId: number;
  employeeCode: any;
  firstName: string;
  lastName: string;
  bookedHours: string;
};
const editColumns: ColDef[] = [
  { field: "employeeCode", headerName: "Employee Code", editable: false },
  { field: "firstName", headerName: "First Name", editable: false },
  { field: "lastName", headerName: "Last Name", editable: false },
  { field: "bookedHours", headerName: "Hours", editable: true },
];
const TimeSheetEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [editTableData, setEditTableData] = useState<EditTableData[]>([]);
  const [isGridEditable, setIsGridEditable] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<number | undefined>();
  const [selectedProject, setSelectedProject] = useState<number | undefined>();
  const [selectedMonth, setSelectedMonth] = useState<number | undefined>();
  const [selectedYear, setSelectedYear] = useState<number | undefined>();
  const [timesheetHeaderData, setTimesheetHeaderData] = useState<any>(null);
  const [editedRows, setEditedRows] = useState<Record<string, EditTableData>>({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteSnackbar, setShowDeleteSnackbar] = useState(false);
  const [errorSnackbarMessage, setErrorSnackbarMessage] = useState<string | null>(null);

  const gridRef = useRef<any>(null);
  const numericId = id ? parseInt(id) : undefined;
  useEffect(() => {
    if (id) {
      loadGetTimesheetDetailsById(+id);
    }
  }, [id]);
  const loadGetTimesheetDetailsById = async (timesheetID: number) => {
    try {
      const response = await getTimeSheetDetailsById(timesheetID);
      const detailArray = response?.data?.timesheetDetailModel;
      setTimesheetHeaderData(response?.data);
      if (Array.isArray(detailArray)) {
        const formattedData = detailArray.map((item: any) => ({
          employeeId: item.employeeId,
          employeeCode: item.employeeCode,
          firstName: item.firstName || "no name",
          lastName: item.lastName,
          bookedHours: item.bookedHours,
        }));
        setEditTableData(formattedData);
      } else {
        setEditTableData([]);
      }
    } catch (error) {
      console.error("Error loading employee details:", error);
    }
  };
  useEffect(() => {
    if (gridRef.current && editTableData.length > 0 && isGridEditable) {
      setTimeout(() => {
        gridRef.current.setFocusedCell(0, "bookedHours");
        gridRef.current.startEditingCell({ rowIndex: 0, colKey: "bookedHours" });
      }, 100);
    }
  }, [editTableData, isGridEditable]);
  useEffect(() => {
    if (timesheetHeaderData) {
      setSelectedCustomer(timesheetHeaderData.customerId);
      setSelectedProject(timesheetHeaderData.projectId);
      setSelectedMonth(timesheetHeaderData.monthId);
      setSelectedYear(timesheetHeaderData.yearId);
      setSelectedRowData(timesheetHeaderData);
    }
  }, [timesheetHeaderData]);

  const handleEditClick = () => {
    setIsGridEditable(true);
    setTimeout(() => {
      gridRef.current?.setFocusedCell(0, "bookedHours");
      gridRef.current?.startEditingCell({ rowIndex: 0, colKey: "bookedHours" });
    }, 0);
  };


  const handleUpdate = async () => {
    if (!numericId) return;
    // 1. Stop editing and allow slight delay to process value change
    gridRef.current?.stopEditing();
    setIsLoading(true);
    // 2. Wait a short timeout to let `onCellValueChanged` update state
    await new Promise((resolve) => setTimeout(resolve, 100)); // 100ms wait
    // 3. Proceed with data merge
    const updatedData = editTableData.map((row) => {
      const edited = editedRows[row.employeeCode];
      return edited ? { ...row, ...edited } : row;
    });
    setEditTableData(updatedData);
    setEditedRows({});
    const now = new Date().toISOString();
    const timesheetDetailModel = updatedData.map((item) => {
      const rawHours = Number(item.bookedHours);
      const safeBookedHours = isNaN(rawHours) ? 0 : rawHours;
      return {
        employeeId: item.employeeId,
        employeeCode: item.employeeCode,
        firstName: item.firstName,
        lastName: item.lastName,
        bookedHours: safeBookedHours,
        timesheetID: numericId,
        createdBy: 0,
        createdOn: now,
        modifiedBy: 0,
        modifiedOn: now,
        isDeleted: false,
        isActive: true,
      };
    });
    const totalHoursBooked = timesheetDetailModel.reduce(
      (sum, emp) => sum + emp.bookedHours,
      0
    );
    const fullPayload = {
      timesheetID: numericId,
      createdBy: 0,
      createdOn: now,
      modifiedBy: 0,
      modifiedOn: now,
      isDeleted: false,
      isActive: true,
      totalHoursBooked,
      customerId: selectedCustomer,
      projectId: selectedProject,
      monthId: selectedMonth,
      yearId: selectedYear,
      timesheetDetailModel,
    };
    try {
      await UpdateTimesheet(numericId, fullPayload);
      setShowSuccessSnackbar(true);
      setIsGridEditable(false);
      setTimeout(() => navigate("/timesheet"), 1500);
    } catch (error) {
      console.error(":x: Error updating timesheet:", error);
    } finally {
      setIsLoading(false);
    }
  };



  const handleConfirmUpdate = async () => {
    setShowConfirmDialog(false);
    await handleUpdate();
  };

  const handleDelete = async () => {
    if (!numericId) return;

    setShowDeleteDialog(false); //  Close confirmation first
    setIsLoading(true); //  Show loader

    try {
      const response = await deleteTimesheetById(numericId);
      console.log("Delete response:", response);

      if (
        response?.status === 200 &&
        response?.data?.data?.toString().toLowerCase().includes("deleted")
      ) {
        setShowDeleteSnackbar(true); // Show success snackbar
        setTimeout(() => navigate("/timesheet"), 1500); 
      } else {
        const errorMessage =
          response?.data?.errors?.[0]?.message || "Failed to delete timesheet";
        setErrorSnackbarMessage(errorMessage); //  Show error
      }
    } catch (error: any) {
      console.error("❌ Error deleting timesheet:", error);

      const errorMessage =
        error?.response?.data?.errors?.[0]?.message || "Cannot delete timesheet.";
      setErrorSnackbarMessage(errorMessage); //  Show error
    } finally {
      setIsLoading(false); 
    }
  };


  return (
    <Box sx={{ padding: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Timeline sx={{ color: "#1976D2" }} />
        <Typography variant="h5" fontWeight="bold">
          Edit Timesheet
        </Typography>
      </Box>
      <InvoiceSummaryCard
        totalHours={selectedRowData?.totalHoursBooked}
        month={selectedRowData?.timesheetMonth}
        year={selectedRowData?.timesheetYear}
        customerName={selectedRowData?.customerName}
        projectName={selectedRowData?.projectName}
      />
      <Box display="flex" gap={2} mt={3} mb={5}>
        <Box flex={2.2}>
          {!isLoading && (
            <AgGridTable
              domLayout="autoHeight"
              customStyles="legacy"
              columns={editColumns}
              data={editTableData}
              pagination={false}
              rowSelection="multiple"
              paginationPerPage={10}
              noDataComponentText="No data added"
              handleSelectedRows={() => { }}
              gridOptions={{
                defaultColDef: {
                  editable: () => isGridEditable,
                  resizable: true,
                  flex: 1,
                },
                stopEditingWhenCellsLoseFocus: true,
                suppressClickEdit: true,
                onGridReady: (params: any) => {
                  gridRef.current = params.api;
                },
                onCellKeyDown: (params: any) => {
                  if (!isGridEditable) return;
                  const colId = params.column.getColId();
                  const currentRowIndex = params.node.rowIndex;
                  if (
                    (params.event?.key === "Enter" || params.event?.key === "ArrowDown") &&
                    colId === "bookedHours"
                  ) {
                    params.event.preventDefault();
                    params.event.stopPropagation();
                    const nextRowIndex = currentRowIndex + 1;
                    const nextRowNode = params.api.getDisplayedRowAtIndex(nextRowIndex);
                    if (nextRowNode) {
                      setTimeout(() => {
                        params.api.setFocusedCell(nextRowIndex, "bookedHours");
                        params.api.startEditingCell({
                          rowIndex: nextRowIndex,
                          colKey: "bookedHours",
                        });
                      }, 0);
                    }
                  }
                },
                onRowClicked: (params: any) => {
                  if (!isGridEditable) return;
                  setTimeout(() => {
                    params.api.setFocusedCell(params.rowIndex, "bookedHours");
                    params.api.startEditingCell({
                      rowIndex: params.rowIndex,
                      colKey: "bookedHours",
                    });
                  }, 0);
                },
                onCellValueChanged: (params: any) => {
                  const newVal = params.newValue ?? "0";
                  setEditedRows((prev) => ({
                    ...prev,
                    [params.data.employeeCode]: {
                      ...params.data,
                      bookedHours:
                        newVal === "" || newVal === null || newVal === undefined
                          ? "0"
                          : String(newVal),
                    },
                  }));
                },
              }}
            />
          )}
        </Box>
      </Box>
      <StickyFooterButtons
        onEditClick={isGridEditable ? () => setShowConfirmDialog(true) : handleEditClick}
        onBackClick={() => navigate("/timesheet")}
        onDeleteClick={() => setShowDeleteDialog(true)} // NEW
        isEditMode={isGridEditable}
      />
      <ConfirmationDialog
        open={showConfirmDialog}
        title="Confirm Update"
        content="Are you sure you want to update this timesheet?"
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmUpdate}
      />
      <ConfirmationDialog
        open={showDeleteDialog}
        title="Confirm Delete"
        content="Are you sure you want to delete this timesheet?"
        onCancel={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
      />
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={2000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setShowSuccessSnackbar(false)} severity="success" sx={{ width: "100%" }}>
          Timesheet updated successfully!
        </Alert>
      </Snackbar>
      <Snackbar
        open={showDeleteSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowDeleteSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setShowDeleteSnackbar(false)}>
          Timesheet deleted successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(errorSnackbarMessage)}
        autoHideDuration={4000}
        onClose={() => setErrorSnackbarMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setErrorSnackbarMessage(null)}>
          {errorSnackbarMessage}
        </Alert>
      </Snackbar>


      {/* Loader */}
      <Backdrop open={isLoading} sx={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>

    </Box>
  );
};
export default TimeSheetEdit